"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  Search,
  ArrowLeft,
  Sparkles,
  ArrowUpDown,
  TrendingUp,
  FileText,
  BarChart3,
} from "lucide-react";
import { getSuggest, searchWithSort } from "@/lib/api";
import type { SuggestItem, SearchResultItem } from "@/types/api";
import { PopularSection } from "@/components/search/popular-section";
import { wikiLink, wikiGroupLink } from "@/lib/url";

type SortKey = "relevance" | "popularity" | "evidence";

const SORT_OPTIONS: { key: SortKey; label: string; icon: typeof TrendingUp }[] = [
  { key: "relevance", label: "관련도순", icon: BarChart3 },
  { key: "popularity", label: "인기순", icon: TrendingUp },
  { key: "evidence", label: "근거등급순", icon: FileText },
];

const EVIDENCE_LABELS: Record<string, { label: string; color: string }> = {
  A: { label: "A", color: "bg-green-100 text-green-700" },
  B: { label: "B", color: "bg-blue-100 text-blue-700" },
  C: { label: "C", color: "bg-amber-100 text-amber-700" },
  D: { label: "D", color: "bg-gray-100 text-gray-500" },
};

export function SearchPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialQ = searchParams.get("q") || "";

  const [query, setQuery] = useState(initialQ);
  const [suggestResults, setSuggestResults] = useState<SuggestItem[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<SortKey>("relevance");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false); // true = 정렬된 검색, false = 자동완성
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // 자동완성 (타이핑 중)
  const doSuggest = useCallback(async (q: string) => {
    if (q.trim().length < 1) {
      setSuggestResults([]);
      setSearchResults([]);
      setIsSearchMode(false);
      return;
    }
    setLoading(true);
    setIsSearchMode(false);
    try {
      const items = await getSuggest(q.trim());
      setSuggestResults(items);
    } catch {
      setSuggestResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 정렬 검색 (정렬 변경 시 또는 Enter)
  const doSearch = useCallback(async (q: string, sortKey: SortKey) => {
    if (q.trim().length < 1) return;
    setLoading(true);
    setIsSearchMode(true);
    try {
      const items = await searchWithSort(q.trim(), sortKey);
      setSearchResults(items);
    } catch {
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 디바운스 자동완성
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSuggest(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSuggest]);

  // 정렬 변경 시 검색 재실행
  const handleSortChange = (key: SortKey) => {
    setSort(key);
    setShowSortMenu(false);
    if (query.trim()) {
      doSearch(query, key);
    }
  };

  // Enter 키로 정렬 검색 실행
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && query.trim()) {
      doSearch(query, sort);
    }
  };

  const hasQuery = query.trim().length > 0;
  const results = isSearchMode ? searchResults : suggestResults;
  const direct = results.filter((r) => !r.related);
  const related = results.filter((r) => r.related);

  return (
    <div className="px-5 py-6">
      <button
        onClick={() => (history.length > 1 ? router.back() : router.push("/wiki"))}
        className="text-muted-foreground hover:text-primary mb-4 inline-flex items-center gap-1 text-sm transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        뒤로
      </button>

      {/* 검색 입력 */}
      <div className="relative mb-4">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="시술명, 고민 검색"
          autoFocus
          className="placeholder:text-muted-foreground focus:border-primary focus:shadow-primary/10 focus:ring-primary/5 w-full rounded-2xl border-[1.5px] border-gray-200 bg-white py-3.5 pr-4 pl-12 text-base transition-all outline-none focus:shadow-lg focus:ring-4"
        />
        <Search className="text-muted-foreground absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2" />
      </div>

      {/* 정렬 옵션 (결과가 있을 때만) */}
      {hasQuery && results.length > 0 && (
        <div className="mb-4 flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            {results.filter((r) => !r.related).length}건의 결과
          </span>
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-violet-300 hover:text-violet-600"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              {SORT_OPTIONS.find((o) => o.key === sort)?.label}
            </button>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowSortMenu(false)} />
                <div className="absolute top-full right-0 z-40 mt-1 w-36 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                  {SORT_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => handleSortChange(opt.key)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors ${
                          sort === opt.key
                            ? "bg-violet-50 font-semibold text-violet-700"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 검색 전 빈 상태 */}
      {!loading && !hasQuery && <PopularSection />}

      {loading && <p className="text-muted-foreground py-8 text-center text-sm">검색 중...</p>}

      {!loading && hasQuery && results.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-muted-foreground mb-4 text-sm">
            &ldquo;{query}&rdquo; 검색 결과가 없습니다
          </p>
          <Link
            href="/chat"
            className="gradient-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/20"
          >
            <Sparkles className="h-4 w-4" />
            AI에게 물어보기
          </Link>
        </div>
      )}

      {results.length > 0 && (
        <div className="flex flex-col">
          {direct.length > 0 && (
            <div className="flex flex-col gap-1">
              {direct.map((item, i) => {
                const ev = "evidence_grade" in item ? (item as SearchResultItem) : null;
                const grade = ev?.evidence_grade;
                const gradeInfo = grade ? EVIDENCE_LABELS[grade] : null;
                return (
                  <Link
                    key={`${item.title}-${i}`}
                    href={item.is_group ? wikiGroupLink(item.title) : wikiLink(item.title)}
                    className="flex items-center gap-3 rounded-xl px-4 py-3.5 transition-colors hover:bg-violet-50"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium">{item.title}</span>
                      {item.aliases && (
                        <span className="text-muted-foreground mt-0.5 block truncate text-xs">
                          {item.aliases}
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {gradeInfo && (
                        <span
                          className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${gradeInfo.color}`}
                        >
                          {gradeInfo.label}
                        </span>
                      )}
                      {ev && ev.total_articles > 0 && (
                        <span className="text-muted-foreground text-[10px]">
                          {ev.total_articles}편
                        </span>
                      )}
                      {item.tags && (
                        <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-1 text-xs">
                          {item.tags}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          {related.length > 0 && (
            <>
              <div className="mt-4 mb-2 flex items-center gap-2 px-4">
                <div className="h-px flex-1 bg-gray-100" />
                <span className="text-muted-foreground text-xs font-semibold tracking-wider">
                  연관 시술
                </span>
                <div className="h-px flex-1 bg-gray-100" />
              </div>
              <div className="flex flex-col gap-1">
                {related.map((item, i) => (
                  <Link
                    key={`related-${item.title}-${i}`}
                    href={wikiLink(item.title)}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-violet-50"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-gray-600">{item.title}</span>
                    </div>
                    {item.tags && (
                      <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-2.5 py-1 text-xs">
                        {item.tags}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
